import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  Divider,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowForward, ArrowUpward, Send, SendOutlined, Telegram } from '@mui/icons-material';
import { get } from 'lodash';
import UserIcon from './user.png';
import BotImg from './Bot2.png';
import { initChatbotSession, sendChatbotMessage } from '../CareerHistory/CareerHistorySlice';

// Turns web addresses written as plain text into markdown links, leaving existing markdown links as they are
const linkifyUrls = (text) =>
  String(text ?? '').replace(/(\[[^\]]*\]\([^)]*\))|(https?:\/\/[^\s<>\]]+)/g, (match, markdownLink, bareUrl) => {
    if (markdownLink) return markdownLink;
    // keep trailing punctuation (and an unmatched closing bracket) out of the link
    let url = bareUrl;
    let tail = '';
    while (/[.,;:!?'")]$/.test(url) && !(url.endsWith(')') && url.includes('('))) {
      tail = url.slice(-1) + tail;
      url = url.slice(0, -1);
    }
    return `[${url}](${url})${tail}`;
  });

const isWebLink = (href) => /^https?:\/\//i.test(String(href || ''));

// fillHeight: take the height of the area the chat sits in (when the widget has a set height), instead of the chat's own fixed heights
const ChatBot = ({ clgBySlug, regionData, currentStep, pageURL, courseId, fillHeight }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hi. Ask me anything about careers....',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [pendingLink, setPendingLink] = useState(null); // a link the visitor clicked, waiting for "same window" or "new window"
  const [sessionId, setSessionId] = useState(null);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // What the chat needs to know about the page it sits on, so "this course" can be answered.
  // Without a page address the referrer (the page that embeds the widget) is sent as a fallback.
  const pageContext = useMemo(
    () => ({
      pageURL,
      courseId,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    }),
    [pageURL, courseId]
  );

  const startSession = async () => {
    try {
      const collegeId = get(clgBySlug, 'data._id');
      const userAgent = navigator.userAgent;
      const data = await initChatbotSession(collegeId, userAgent, pageContext);
      const newSessionId = data?.data?.sessionId;
      setSessionId(() => newSessionId);
      return newSessionId;
    } catch (err) {
      console.error('Failed to init chatbot session', err);
      return null;
    }
  };

  useEffect(() => {
    const startCurrentSession = async () => {
      try {
        const collegeId = get(clgBySlug, 'data._id');
        const userAgent = navigator.userAgent;
        const data = await initChatbotSession(collegeId, userAgent, pageContext);
        setSessionId(() => data?.data?.sessionId);
      } catch (err) {
        console.error('Failed to init chatbot session', err);
      }
    };
    if (get(clgBySlug, 'data._id') && !sessionId && currentStep === 2) {
      startCurrentSession();
    }
  }, [sessionId, clgBySlug, currentStep, pageContext]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    const userMessage = { sender: 'user', text: inputValue };
    const botTypingMessage = { sender: 'bot', text: '...', isTyping: true };
    setMessages((prev) => [...prev, userMessage, botTypingMessage]);
    setInputValue('');
    setIsBotTyping(true);

    try {
      // Auto-start the session if the chat tab has not done it yet
      const currentSessionId = sessionId || (await startSession());

      const res = await sendChatbotMessage(currentSessionId, inputValue);
      const botResponseText = res?.data?.response || 'No response';
      setMessages((prev) => [
        ...prev.slice(0, -1), // remove last "typing" message
        { sender: 'bot', text: botResponseText },
      ]);
      setInputValue('');
    } catch (err) {
      console.error('Chatbot message error', err);
      const errorMessage = get(err, 'response.data.message')
      setMessages((prev) => [
        ...prev.slice(0, -1), // remove "typing"
        { sender: 'bot', text: errorMessage || 'Something went wrong. Please try again.' },
      ]);
    } finally {
      setIsBotTyping(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const openPendingLink = (inNewWindow) => {
    const url = pendingLink;
    setPendingLink(null);
    if (!url) return;
    if (inNewWindow) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    // "Same window": the widget usually sits in a frame on the college's page, so the whole page is navigated
    try {
      window.top.location.assign(url);
    } catch (err) {
      window.location.assign(url);
    }
  };

  const MessageBubble = ({ message }) => {
    const isBot = message?.sender === 'bot';
    const renderMessageContent = () => {
      if (isBot && message?.isTyping) {
        return <Typography>Typing...</Typography>;
      }

      if (isBot) {
        return (
          <ReactMarkdown
            children={linkifyUrls(message.text)}
            components={{
              p: ({ children }) => (
                <Typography sx={{ fontSize: 14, color: '#fff', mb: 1, textAlign: 'left' }}>{children}</Typography>
              ),
              strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
              li: ({ children }) => <li style={{ marginBottom: 4, fontSize: 14 }}>{children}</li>,
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    // Web links ask first: same window or new window
                    if (isWebLink(href)) {
                      event.preventDefault();
                      setPendingLink(href);
                    }
                  }}
                  style={{ color: regionData?.button?.bgColor || '#FFD600' }}
                >
                  {children}
                </a>
              ),
            }}
          />
        );
      }

      return (
        <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#fff', textAlign: 'left' }}>
          {message.text}
        </Typography>
      );
    };
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isBot ? 'flex-start' : 'flex-end',
          mb: 2,
          alignItems: 'flex-end',
          gap: 1.5,
        }}
      >
        {isBot && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}
          >
            <Avatar
              src={BotImg}
              sx={{ width: 48, height: 48, bgcolor: 'transparent', mb: 0.5, boxShadow: 'none' }}
            />
          </Box>
        )}

        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            maxWidth: 420,
            minWidth: 120,
            background: isBot
              ? 'linear-gradient(90deg, #0a1760 0%, #283891 100%)'
              : 'linear-gradient(90deg, #1c7f99 0%, #59b6ce 100%)',
            color: '#fff',
            borderRadius: isBot ? '16px 16px 16px 0px' : '16px 16px 0px 16px',
            fontSize: '15px',
            fontWeight: 400,
            position: 'relative',
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.07)',
            ml: isBot ? 0 : 1,
            mr: isBot ? 1 : 0,
            '&::after': isBot
              ? {
                content: '""',
                position: 'absolute',
                left: -16,
                top: 18,
                width: 0,
                height: 0,
                borderTop: '12px solid transparent',
                borderBottom: '12px solid transparent',
                borderRight: '16px solid #0a1760',
              }
              : {
                content: '""',
                position: 'absolute',
                right: -16,
                top: 18,
                width: 0,
                height: 0,
                borderTop: '12px solid transparent',
                borderBottom: '12px solid transparent',
                borderLeft: '16px solid #59b6ce',
              },
          }}
        >
          {renderMessageContent()}
        </Paper>

        {!isBot && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}
          >
            <Avatar
              src={UserIcon}
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 0.5,
                boxShadow: 'none',
              }}
            >
              {/* <PersonIcon sx={{ color: '#fff', fontSize: 20 }} /> */}
            </Avatar>
          </Box>
        )}
      </Box>
    );
  };

  MessageBubble.propTypes = {
    message: PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      sender: PropTypes.oneOf(['bot', 'user']).isRequired,
      timestamp: PropTypes.instanceOf(Date).isRequired,
      isTyping: PropTypes.bool,
    }).isRequired,
  };

  return (
    <Box
      sx={{
        height: fillHeight ? '100%' : {lg: 795, sm:650, md: 670, xs: 700},
        // maxHeight: 860,
        minHeight: fillHeight ? 260 : 550,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRadius: '0 0 8px 8px',
        overflow: 'hidden',
        // border: '2px solid #23236A',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        flex: fillHeight ? '1 1 auto' : '0 0 550px',
        position: 'relative',
      }}
    >
      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: '#fff',
          width: '100%',
          boxSizing: 'border-box',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
          },
        }}
      >
        {messages?.map((message) => (
          // eslint-disable-next-line react/prop-types
          <MessageBubble key={message?.id} message={message} />
        ))}
        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar src={BotImg} sx={{ width: 32, height: 32, bgcolor: 'transparent' }} />
            <Paper
              elevation={1}
              sx={{
                p: 1,
                bgcolor: '#23236A',
                borderRadius: '16px 16px 16px 0px',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      bgcolor: '#fff',
                      borderRadius: '50%',
                      animation: `typing 1.4s infinite ${i * 0.2}s`,
                      '@keyframes typing': {
                        '0%, 60%': { opacity: 0.3 },
                        '30%': { opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: '#fff',
          // borderTop: '2px solid #23236A',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextField
          fullWidth
          variant="standard"
          placeholder="Enter your message here..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isBotTyping}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          InputProps={{
            disableUnderline: true,
            sx: {
              bgcolor: '#fff',
              border: '2px solid #23236A',
              borderRadius: '8px',
              px: 2,
              py: 1.2,
              fontSize: '16px !important',
              fontWeight: 500,
              color: '#23236A',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              '&::placeholder': {
                color: '#000000 !important',
                opacity: 1,
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isBotTyping}
                  sx={{
                    bgcolor: '#23236A',
                    color: '#FFD600',
                    borderRadius: '8px',
                    ml: 1,
                    '&:hover': {
                      bgcolor: '#1a1a4d',
                    },
                    '&.Mui-disabled': {
                      color: '#FFD600',
                      opacity: 1,
                      bgcolor: '#23236A',
                      '& .MuiSvgIcon-root': {
                        color: '#FFD600', // ensures Telegram icon stays yellow
                      },
                    },
                  }}
                >
                  <Telegram sx={{ fontSize: 22 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box
        sx={{
          px: 1.5,
          pb: 1,
          bgcolor: '#fff',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px !important',
            color: '#666',
            textAlign: 'left',
            lineHeight: 1.3,
            fontStyle: 'italic',
          }}
        >
          Powered by AI, this ThinkSkills chatbot offers guidance to help you discover courses and career options. Information shared is for general insight only — always confirm key details with your college or a careers advisor.
        </Typography>
      </Box>

      <Dialog open={Boolean(pendingLink)} onClose={() => setPendingLink(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Open this link?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1.5 }}>Would you like to open it in this window or in a new window?</Typography>
          <Typography sx={{ fontSize: 13, color: '#555', wordBreak: 'break-all' }}>{pendingLink}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setPendingLink(null)}>Cancel</Button>
          <Button variant="outlined" onClick={() => openPendingLink(false)}>
            Same window
          </Button>
          <Button variant="contained" autoFocus onClick={() => openPendingLink(true)}>
            New window
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatBot;
ChatBot.propTypes = {
  clgBySlug: PropTypes.any,
  regionData: PropTypes.object,
  currentStep: PropTypes.number,
  pageURL: PropTypes.string,
  courseId: PropTypes.string,
  fillHeight: PropTypes.bool,
};
